import { message, Tooltip } from 'antd';
import dayjs from 'lib/dayjs';
import { RightOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { useCallback, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { Menu, Item, Separator } from 'react-contexify';
import { useSetConversationQuotedMsgDraft } from 'hooks/business/conversationDraft';
import { LocalMessage, useConversationReadonlyValue } from 'hooks/business/conversation';
import {
  getTiktokNormalQualityVideo,
  lazadaVideoUrl,
  recallMessage,
} from 'services/api/conversation';
import copyText from 'utils/copyText';
import type { DialogueImageMessage, DialogueVideoMessage } from 'components/Dialogue/types';
import download from 'utils/download';
import { getImageBlob } from 'utils/getImage';
import { UIMessages } from '../../useChatView';
import TranslatePopover, { TranslatePopoverProps } from './TranslatePopover';
import { openExternalURL } from 'utils/openExternalURL';
import posthog from '@leyan/lytics';
import useFocusTask from 'hooks/useFocusTask';
import { useSortedShopeeDomains } from 'components/SafeImage/useShopeeDomainScore';
import { replaceShopeeHost } from 'components/SafeImage/SafeImage';
import { isQuickChat } from 'productLine';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';
import { isInBrowser } from 'utils/is';
import { getTiktokProxyImageSrc, isShopeePic } from 'lib/utils/platformImage';
import { getMsgById } from 'services/api/conversation.convList';
import { useChatContext } from '../../contexts/ChatContext';
import { noop } from 'lodash-es';
import { useOrgConfigStateValue } from 'hooks/business/useOrgConfig';
import { useUpdateMessage } from 'hooks/business/message';
import useProxySrc from 'hooks/business/useProxySrc';

export const contextMenuId = '@@messageContextMenuId';

const supportRecallPlatforms: General.PlatformUpperCaseTunnel[] = ['LAZADA', 'KWAI'];

const shopeeRecallMinuteLimit: Partial<Record<General.UpperCaseRegion, number>> = {
  BR: 10,
  ID: 10,
  PH: 10,
  SG: 10,
  TH: 3,
};

export type ContextMenuSingleType = 'image' | 'text';

export const isSupportMenu = (
  uiMessage: UIMessages,
  conversation: Conversation.Conversation,
  isGalaxy: boolean,
  readonly = false,
  videoText?: string,
  singleType?: ContextMenuSingleType,
  galaxyModule?: Conversation.GalaxyModule,
) => {
  const { type, status, sender, raw } = uiMessage;
  const isQI = import.meta.env.VITE_QI === 'true';

  const { channel = 'SHOPEE', region, controlStatus, isSellerAffiliateChat } = conversation;

  const isSupportRecall =
    !isGalaxy &&
    controlStatus &&
    (supportRecallPlatforms.includes(channel) ||
      (channel === 'SHOPEE' && !isSellerAffiliateChat && shopeeRecallMinuteLimit[region])) &&
    (sender === 'seller' || sender === 'robot') &&
    status === 'normal';

  const isSupportCopy =
    (type === 'text' && raw.textContent?.text) || type === 'image' || type === 'imageWithText';

  const isSupportCopyUrl =
    type === 'video' || type === 'image' || (type === 'imageWithText' && singleType === 'image');

  const isSupportDownload = type === 'image' || type === 'video' || type === 'imageWithText';

  const isSupportOpenBrowser = isTiKTokUpperCase(channel) && type === 'video';

  const isSupportTranslate =
    (type === 'text' || type === 'imageWithText' || type === 'attachment') && !isQI;

  const isSupportFeedback = !isGalaxy && !isQuickChat && !isQI;

  const isSupportReply = !readonly && type === 'text' && controlStatus && !isGalaxy && !isQI;

  const isSupportVideo2Text =
    raw.messageType === 'VIDEO' && raw.msgFromType === 'BUYER' && videoText === undefined && !isQI;
  const isSupportHideVideoText =
    raw.messageType === 'VIDEO' && raw.msgFromType === 'BUYER' && videoText !== undefined && !isQI;

  const isSupportIntentReport =
    isGalaxy &&
    !galaxyModule &&
    raw.messageType === 'TEXT' &&
    raw.msgFromType === 'BUYER' &&
    raw.llmDetail?.isNewFlow === 1;

  const isSupportContext =
    isSupportRecall ||
    isSupportCopy ||
    isSupportDownload ||
    isSupportOpenBrowser ||
    isSupportVideo2Text ||
    isSupportHideVideoText;

  const isSupportQi = isQI;

  const isSupportExtra =
    isSupportFeedback ||
    isSupportTranslate ||
    isSupportReply ||
    isQI ||
    isSupportVideo2Text ||
    isSupportHideVideoText ||
    isSupportIntentReport;

  return {
    isSupportContext,
    isSupportExtra,
    isSupportRecall,
    isSupportCopy,
    isSupportCopyUrl,
    isSupportDownload,
    isSupportOpenBrowser,
    isSupportTranslate,
    isSupportReply,
    isSupportFeedback,
    isSupportQi,
    isSupportVideo2Text,
    isSupportHideVideoText,
    isSupportIntentReport,
  };
};

export const getVideoUrl = ({
  channel,
  msgId,
  conversationId,
  videoId,
  quality,
  videoUrl,
}: {
  channel: General.PlatformUpperCaseTunnel;
  msgId: string;
  conversationId: string;
  videoId?: string;
  quality?: string;
  videoUrl: string;
}) => {
  if (channel === 'SHOPEE') {
    return getMsgById(msgId).then(({ videoContent }) => {
      if (videoContent) {
        return videoContent?.videoUrl;
      } else {
        return Promise.reject(new Error('Invalid msg'));
      }
    });
  }

  if (isTiKTokUpperCase(channel) && quality !== 'normal') {
    return getTiktokNormalQualityVideo({
      conversationId,
      msgId,
    }).then(({ videoUrl }) => videoUrl);
  }

  if (channel === 'LAZADA') {
    if (!videoId) {
      return Promise.reject(new Error('no videoId'));
    }

    return lazadaVideoUrl({
      msgId,
      videoId,
      conversationId,
    }).then(({ videoUrl }) => videoUrl);
  }

  return Promise.resolve(videoUrl);
};

export const useCopyDownloadImage = () => {
  const { t } = useTranslate();

  const sortedShopeeDomains = useSortedShopeeDomains();

  const { executeFocusTask } = useFocusTask();

  const getShopeeImage = useCallback(
    (url: string) => {
      return sortedShopeeDomains.reduce<Promise<string>>((res, { domain }) => {
        return res.then((res) => res).catch(() => replaceShopeeHost(url, domain));
      }, Promise.reject());
    },
    [sortedShopeeDomains],
  );

  const { getProxyEnable } = useProxySrc();

  const copyImage = useCallback(
    (imageUrl: string) => {
      return Promise.resolve(isShopeePic(imageUrl) ? getShopeeImage(imageUrl) : imageUrl)
        .then((url) =>
          getImageBlob(getProxyEnable(url) ? getTiktokProxyImageSrc(url) : url, isInBrowser),
        )
        .then((blob) => {
          return executeFocusTask(() =>
            navigator.clipboard.write([
              new ClipboardItem({
                'image/png': blob,
              }),
            ]),
          );
        })
        .then(() => {
          message.success(t('common.feedback.copySuccess'));
        })
        .catch((err) => {
          posthog.capture('$copyImageError', {
            errMsg: err?.message,
          });
          message.error(t('common.feedback.copyFailed'));
        });
    },
    [executeFocusTask, getShopeeImage, t, getProxyEnable],
  );

  const downloadImage = useCallback(
    (imageUrl: string) => {
      const hideLoading = message.loading(
        t('scene.chatWorkspace.messageThread.status.downloading'),
        0,
      );

      return Promise.resolve(isShopeePic(imageUrl) ? getShopeeImage(imageUrl) : imageUrl)
        .then((url) => download(url, '', true))
        .catch(() => {
          message.error(t('scene.chatWorkspace.messageThread.feedback.downloadFailed'));
        })
        .finally(() => {
          hideLoading();
        });
    },
    [getShopeeImage, t],
  );

  return {
    t,
    copyImage,
    downloadImage,
  };
};

export interface ContextMenuProps {
  uiMessage: UIMessages;
  conversation: Conversation.Conversation;
  isGalaxy?: boolean;
  onTranslate: TranslatePopoverProps['onTranslate'];
  onFeedback: (message: LocalMessage) => void;
  singleType?: ContextMenuSingleType;
  targetLanguage: General.Language;
}

function ContextMenu(props: ContextMenuProps) {
  const {
    uiMessage,
    conversation,
    isGalaxy = false,
    singleType,
    targetLanguage,
    onTranslate,
    onFeedback,
  } = props;

  const { type, time, dialogueUuid, raw } = uiMessage;

  const { conversationId, storeId, channel, region } = conversation;

  const setQuotedMessageDraft = useSetConversationQuotedMsgDraft();
  const updateMessage = useUpdateMessage();

  const readonly = useConversationReadonlyValue();

  const { videoTextState: [videoText, setVideoText = noop] = [] } = useChatContext();

  const { whiteListConfig: { ASR } = {} } = useOrgConfigStateValue();

  const {
    isSupportRecall,
    isSupportCopy,
    isSupportCopyUrl,
    isSupportDownload,
    isSupportOpenBrowser,
    isSupportTranslate,
    isSupportFeedback,
    isSupportReply,
    isSupportContext,
    isSupportVideo2Text,
    isSupportHideVideoText,
  } = useMemo(
    () => isSupportMenu(uiMessage, conversation, isGalaxy, readonly, videoText, singleType),
    [uiMessage, conversation, isGalaxy, readonly, videoText, singleType],
  );

  const { t, copyImage, downloadImage } = useCopyDownloadImage();

  if (!isSupportContext) {
    return null;
  }

  const menu = [];

  if (isSupportRecall) {
    const minutes = channel === 'SHOPEE' ? (shopeeRecallMinuteLimit[region] as number) : 2;
    const notAllow = dayjs(time).add(minutes, 'minutes').isBefore(dayjs());

    if (notAllow) {
      menu.push(
        <Tooltip
          title={t('scene.chatWorkspace.messageThread.validation.recallTimeLimitExceeded').replace(
            '2',
            `${minutes}`,
          )}
        >
          <Item disabled>{t('common.action.recall')}</Item>
        </Tooltip>,
      );
    } else {
      menu.push(
        <Item
          key="recall"
          onClick={() => {
            recallMessage({
              storeId,
              conversationId,
              messageId: dialogueUuid,
            })
              .then(() => {
                updateMessage(dialogueUuid, (prev) => {
                  if (!prev) {
                    return undefined;
                  }
                  return {
                    ...prev,
                    isRecall: true,
                  };
                });
              })
              .catch(() => {
                message.error(t('scene.chatWorkspace.messageThread.feedback.recallFailed'));
              });
          }}
        >
          {t('common.action.recall')}
        </Item>,
      );
    }
  }

  const separator = <Separator />;

  const isSupportGroup2 =
    isSupportCopy ||
    isSupportCopyUrl ||
    isSupportDownload ||
    isSupportOpenBrowser ||
    isSupportTranslate ||
    isSupportReply;

  if (isSupportRecall && isSupportGroup2) {
    menu.push(separator);
  }

  if (isSupportCopy) {
    menu.push(
      <Item
        key="copy"
        onClick={() => {
          if (type === 'text' || (type === 'imageWithText' && singleType === 'text')) {
            const selectedText = window.getSelection()?.toString();

            const translationText = raw.textContent?.originalText || raw.textContent?.text || '';

            copyText(selectedText || translationText)
              .then(() => {
                message.success(t('common.feedback.copySuccess'));
              })
              .catch(() => {
                message.error(t('common.feedback.copyFailed'));
              });
          }

          if (type === 'image' || (type === 'imageWithText' && singleType === 'image')) {
            const imageUrl =
              (uiMessage as DialogueImageMessage)?.content?.url || raw.imageContent?.imgUrl;

            if (!imageUrl) {
              message.error(t('common.feedback.copyFailed'));

              return;
            }

            copyImage(imageUrl);
          }
        }}
      >
        {t('common.action.copy')}
      </Item>,
    );
  }

  const videoUrl = (uiMessage as DialogueVideoMessage)?.content?.url;

  const { msgId, videoContent: { videoId, quality } = {} } = raw;

  const _getVideoUrl = () =>
    getVideoUrl({
      channel,
      msgId,
      conversationId,
      videoId,
      quality,
      videoUrl,
    });

  if (isSupportCopyUrl) {
    menu.push(
      <Item
        key="copy"
        onClick={() => {
          if (type === 'image' || type === 'imageWithText') {
            const imageUrl =
              (uiMessage as DialogueImageMessage)?.content?.url || raw.imageContent?.imgUrl;
            if (!imageUrl) {
              message.error(t('common.feedback.copyFailed'));
            } else {
              copyText(imageUrl);
              message.success(t('common.feedback.copySuccess'));
            }
            return;
          }
          _getVideoUrl()
            .then((videoUrl) => {
              copyText(videoUrl);
              message.success(t('common.feedback.copySuccess'));
            })
            .catch(() => {
              message.error(t('common.feedback.copyFailed'));
            });
        }}
      >
        {t('common.action.copyLink')}
      </Item>,
    );
  }

  if (isSupportDownload && !(type === 'imageWithText' && singleType !== 'image')) {
    menu.push(
      <Item
        key="download"
        onClick={() => {
          if (type === 'image' || (type === 'imageWithText' && singleType === 'image')) {
            const imageUrl =
              (uiMessage as DialogueImageMessage)?.content?.url || raw.imageContent?.imgUrl;

            if (imageUrl) {
              downloadImage(imageUrl);
            }
          }

          if (type === 'video') {
            const hideLoading = message.loading(
              t('scene.chatWorkspace.messageThread.status.downloading'),
              0,
            );

            _getVideoUrl()
              .then((url) => download(url, `${Date.now()}.mp4`, true))
              .catch(() => {
                message.error(t('scene.chatWorkspace.messageThread.feedback.downloadFailed'));
              })
              .finally(() => {
                hideLoading();
              });
          }
        }}
      >
        {t('common.action.download')}
      </Item>,
    );
  }

  if (ASR) {
    if (isSupportVideo2Text) {
      menu.push(
        <Item key="video2text" onClick={() => setVideoText('')}>
          {t('scene.chatWorkspace.messageMenu.action.videoToText')}
        </Item>,
      );
    } else if (isSupportHideVideoText) {
      menu.push(
        <Item key="video2text" onClick={() => setVideoText()}>
          {t('scene.chatWorkspace.messageMenu.action.cancelVideoToText')}
        </Item>,
      );
    }
  }

  if (isSupportOpenBrowser) {
    menu.push(
      <Item
        key="openBrowser"
        onClick={() => {
          _getVideoUrl()
            .then((url) => {
              openExternalURL(url, '_blank');
            })
            .catch(() => {});
        }}
      >
        {t('common.action.openInBrowser')}
      </Item>,
    );
  }

  if (isSupportReply) {
    menu.push(
      <Item
        key="reply"
        onClick={() => {
          setQuotedMessageDraft(raw.conversationId, raw.msgId);
        }}
      >
        {t('common.action.reply')}
      </Item>,
    );
  }
  if (isSupportTranslate && !(type === 'imageWithText' && singleType !== 'text')) {
    menu.push(
      <TranslatePopover
        key="translate"
        placement="right"
        align={{ offset: [-5, 0] }}
        message={raw}
        targetLanguage={targetLanguage}
        onTranslate={onTranslate}
        zIndex={999999}
      >
        <Item disabled style={{ opacity: 1 }} onClick={({ event }) => event.stopPropagation()}>
          <Translate>
            <span>{t('common.action.translate')}</span>
            <RightOutlined />
          </Translate>
        </Item>
      </TranslatePopover>,
    );
  }

  if (isSupportGroup2 && isSupportFeedback) {
    menu.push(separator);
  }

  if (isSupportFeedback) {
    menu.push(
      <Item key="feedback" onClick={() => onFeedback(raw)}>
        {t('scene.chatWorkspace.feedbackDialog.title')}
      </Item>,
    );
  }

  return <Menu id={contextMenuId}>{menu}</Menu>;
}
const Translate = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  cursor: pointer;
  > .anticon {
    font-size: 12px;
  }
`;

export default ContextMenu;
